import {
  Accordion,
  Button,
  Checkbox,
  Divider,
  FixedFooter,
  Input,
  InputNumber,
  Select,
  Textarea,
  Timeline,
} from "@components/";
import { useNewOrder } from "./useNewOrder";
import { ProductSearch } from "./ProductSearch";
import { UserSearch } from "./UserSearch";
import { OrderSummary } from "./OrderSummary";
import { formatDateBR } from "../../hooks";
import {
  mapClassificacaoTroca,
  mapPlataforma,
  mapRetira,
  mapStatusIntegracao,
  mapAvaliacaoCredito,
  mapTipoRegistro,
  mapTipoEnvio,
} from "../../utils/fieldMappings";
import {
  classificacaoTroca,
  fields,
  plataformas,
  retiraValues,
  statusIntegracao,
  values,
} from "./MockValues";
import { FaCheck } from "react-icons/fa6";
import { Controller } from "react-hook-form";
import type { ProductResult } from "./interfaces";
import { useRef, useEffect } from "react";

export function NewOrder() {
  // Prevenir smooth scrolling durante toda a sessão
  useEffect(() => {
    const originalScrollBehavior =
      document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = "auto";
    document.body.style.scrollBehavior = "auto";

    return () => {
      document.documentElement.style.scrollBehavior = originalScrollBehavior;
      document.body.style.scrollBehavior = "";
    };
  }, []);

  // Ref para o container da página
  const pageRef = useRef<HTMLDivElement>(null);

  const {
    navigate,
    step,
    setStep,
    success,
    isLoading,
    selectedProducts,
    savedOrderData,
    selectedSolicitante,
    setSelectedSolicitante,
    tipoTicketOptions,
    createdOrders,
    register,
    handleSubmit,
    errors,
    onSubmit,
    handleFinalSubmit,
    addProduct,
    removeProduct,
    updateProduct,
    setValue,
    control,
    watch,
  } = useNewOrder();

  const formRef = useRef<HTMLFormElement>(null);

  if (success) {
    return (
      <div className="flex flex-col gap-[6.5px] bg-[#EDF8F4] py-[22.5px] px-4 h-full">
        <h1 className="text-[18px] font-semibold">
          Pedidos <span className="text-[#87929D]">• Adicionar pedido</span>
        </h1>
        <div className="flex flex-col items-center justify-center gap-4">
          <div className="w-11 h-11 rounded-full border-2 flex items-center justify-center border-[#107048]">
            <FaCheck size={25} className="text-xs text-[#107048]" />
          </div>
          <h2 className="text-[22px] font-semibold text-[#107048]">
            {createdOrders.length > 1
              ? `${createdOrders.length} Pedidos clonados com sucesso!`
              : "Pedido adicionado com sucesso!"}
          </h2>

          {/* Indicação de abertura automática do ticket */}
          <div className="text-sm text-[#107048] bg-[#f0f9f5] px-3 py-2 rounded-md">
            <p>
              🔗{" "}
              <strong>Abrindo ticket automaticamente em 5 segundos...</strong>
            </p>
            <p className="text-xs opacity-75 mt-1">
              Você será redirecionado para o ticket problem criado
            </p>
          </div>

          {createdOrders.length > 1 && (
            <div className="text-sm text-[#107048] bg-[#f0f9f5] px-3 py-2 rounded-md">
              <p>
                📋 <strong>Clone ativado:</strong> {createdOrders.length} side
                conversations criadas
              </p>
              <p className="mt-1">
                IDs dos pedidos:{" "}
                {createdOrders.map((order) => order.pedidoId).join(", ")}
              </p>
            </div>
          )}
        </div>

        <div className="mt-8">
          <Accordion
            title={
              savedOrderData
                ? `Pedido ${savedOrderData.pedidoInterno}`
                : "Pedido"
            }
            fields={[
              "Estabelecimento",
              "Tipo de Envio",
              "Pedido Interno",
              "Classificação da Troca",
              "Avaliação de Crédito",
              "Plataforma",
              "Retira",
              "Número de Parcelas",
              "ECOM PJ",
              "Status da Integração",
              "Tipo do Registro",
              "Data da Integração",
              ...(savedOrderData?.observacoes ? ["Observações"] : []),
              ...(savedOrderData?.evidenciaMotivo
                ? ["Evidência do Motivo"]
                : []),
            ]}
            values={[
              savedOrderData?.estabelecimento || "-",
              savedOrderData?.tipoEnvio
                ? mapTipoEnvio(savedOrderData.tipoEnvio)
                : "-",
              savedOrderData?.pedidoInterno || "-",
              savedOrderData?.classificacaoTroca
                ? mapClassificacaoTroca(savedOrderData.classificacaoTroca)
                : "-",
              savedOrderData?.avaliacaoCredito
                ? mapAvaliacaoCredito(savedOrderData.avaliacaoCredito)
                : "-",
              savedOrderData?.plataforma
                ? mapPlataforma(savedOrderData.plataforma)
                : "-",
              savedOrderData?.retira ? mapRetira(savedOrderData.retira) : "-",
              savedOrderData?.numeroParcelas?.toString() || "0",
              savedOrderData?.ecomPj ? "Sim" : "Não",
              savedOrderData?.statusIntegracao
                ? mapStatusIntegracao(savedOrderData.statusIntegracao)
                : "-",
              savedOrderData?.tipoRegistro
                ? mapTipoRegistro(savedOrderData.tipoRegistro)
                : "-",
              savedOrderData?.dataIntegracao
                ? formatDateBR(savedOrderData.dataIntegracao)
                : "-",
              ...(savedOrderData?.observacoes
                ? [savedOrderData.observacoes]
                : []),
              ...(savedOrderData?.evidenciaMotivo
                ? [savedOrderData.evidenciaMotivo]
                : []),
            ]}
            defaultOpen={true}
          ></Accordion>
        </div>
        <div>
          {selectedProducts && selectedProducts.length > 0 ? (
            selectedProducts.map((product, index) => (
              <Accordion
                key={index}
                title={`${product.name}`}
                fields={[
                  "Nome",
                  "Código do produto",
                  "Marca",
                  "Linha",
                  "Família do produto",
                  "Código ERP",
                  "Ativo",
                  "Peso",
                  "Altura",
                  "Largura",
                  "Profundidade",
                  "Quantidade",
                  "Preço",
                ]}
                values={[
                  product.name,
                  String(
                    product.custom_object_fields?.codigo_do_produto || "-"
                  ),
                  String(product.custom_object_fields?.marca || "-"),
                  String(product.custom_object_fields?.linha || "-"),
                  String(
                    product.custom_object_fields?.familia_do_produto ||
                      product.custom_object_fields?.familia_produto ||
                      "-"
                  ),
                  String(product.custom_object_fields?.codigo_erp || "-"),
                  product.custom_object_fields?.ativo ? "Sim" : "Não",
                  product.custom_object_fields?.peso
                    ? `${product.custom_object_fields.peso} g`
                    : "-",
                  product.custom_object_fields?.altura
                    ? `${product.custom_object_fields.altura} mm`
                    : "-",
                  product.custom_object_fields?.largura
                    ? `${product.custom_object_fields.largura} mm`
                    : "-",
                  product.custom_object_fields?.profundidade
                    ? `${product.custom_object_fields.profundidade} mm`
                    : "-",
                  product.quantidade.toString(),
                  product.valorUnitario,
                ]}
                defaultOpen={false}
              />
            ))
          ) : (
            <div className="text-gray-500 text-sm p-2">
              Nenhum produto adicionado
            </div>
          )}
        </div>

        <FixedFooter pageRef={pageRef}>
          <Button variant="outline" onClick={() => navigate("/")}>
            Voltar para pedidos
          </Button>
        </FixedFooter>
      </div>
    );
  }

  return (
    <div ref={pageRef} className="flex flex-col gap-[6.5px] py-[22.5px] px-4">
      <h1 className="text-[18px] font-semibold">
        Pedidos <span className="text-[#87929D]">• Novo pedido</span>
      </h1>

      <div className="flex flex-col items-center justify-center mt-4 mb-8 gap-4">
        <Timeline totalSteps={3} currentStep={step} />
        <h4 className="font-semibold text-sm">Preencha os dados do pedido</h4>
      </div>

      {step === 1 && (
        <form
          className="flex flex-col gap-4 mb-4"
          onSubmit={handleSubmit(onSubmit)}
          ref={formRef}
        >
          <div>
            <Controller
              name="solicitante"
              control={control}
              render={({ field }) => (
                <UserSearch
                  onSelect={(user) => {
                    console.log(
                      "🔧 DEBUG: Usuário selecionado no NewOrder:",
                      user
                    );
                    setSelectedSolicitante(user);
                    field.onChange(user.name); // Atualiza o valor do formulário
                  }}
                  onClear={() => {
                    console.log("🔧 DEBUG: Campo de usuário limpo");
                    setSelectedSolicitante(null);
                    field.onChange(""); // Limpa o valor do formulário
                  }}
                  placeholder="Busque por nome ou email do solicitante"
                  initialValue={
                    selectedSolicitante ? selectedSolicitante.name : ""
                  }
                />
              )}
            />
            {errors.solicitante && (
              <span className="text-red-500 text-sm">
                {errors.solicitante.message}
              </span>
            )}
          </div>

          <div>
            <Select
              value={watch("estabelecimento")}
              label="Estabelecimento"
              {...register("estabelecimento")}
            >
              <option value="">Selecione um estabelecimento</option>
              <option value="15">15</option>
              <option value="e0001">E0001</option>
            </Select>
            {errors.estabelecimento && (
              <span className="text-red-500 text-sm">
                {errors.estabelecimento.message}
              </span>
            )}
          </div>

          <div>
            <Select
              value={watch("tipoEnvio")}
              label="Tipo de envio do pedido"
              {...register("tipoEnvio")}
            >
              <option value="">Selecione o tipo de envio</option>
              <option value="pedido_tipo_de_envio_do_pedido_cortesia">
                Cortesia
              </option>
              <option value="pedido_tipo_de_envio_do_pedido_garantia">
                Garantia
              </option>
              <option value="pedido_tipo_de_envio_do_pedido_venda">
                Venda
              </option>
            </Select>
            {errors.tipoEnvio && (
              <span className="text-red-500 text-sm">
                {errors.tipoEnvio.message}
              </span>
            )}
          </div>

          <div>
            <Input
              label="Pedido interno"
              placeholder="Digite pedido"
              {...register("pedidoInterno")}
            />
            {errors.pedidoInterno && (
              <span className="text-red-500 text-sm">
                {errors.pedidoInterno.message}
              </span>
            )}
          </div>

          <Checkbox label="Clonar pedido" {...register("clonarPedido")} />

          <div>
            <Select
              value={watch("classificacaoTroca")}
              label="Classificação da troca"
              {...register("classificacaoTroca")}
            >
              <option value="">Selecione a classificação</option>
              {classificacaoTroca.map((option, index) => (
                <option key={index} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            {errors.classificacaoTroca && (
              <span className="text-red-500 text-sm">
                {errors.classificacaoTroca.message}
              </span>
            )}
          </div>

          <div>
            <Textarea
              value={watch("evidenciaMotivo")}
              label="Evidência do motivo"
              placeholder="Digite as observações"
              {...register("evidenciaMotivo")}
            />
            {errors.evidenciaMotivo && (
              <span className="text-red-500 text-sm">
                {errors.evidenciaMotivo.message}
              </span>
            )}
          </div>

          <div>
            <Input
              label="Valor do frete"
              placeholder="Ex: 100.00"
              {...register("valorFrete")}
            />
            {errors.valorFrete && (
              <span className="text-red-500 text-sm">
                {errors.valorFrete.message}
              </span>
            )}
          </div>

          <div>
            <Input
              label="Canal de venda"
              placeholder="Digite o canal de venda"
              {...register("canalVenda")}
            />
            {errors.canalVenda && (
              <span className="text-red-500 text-sm">
                {errors.canalVenda.message}
              </span>
            )}
          </div>

          <div>
            <Select
              value={watch("avaliacaoCredito")}
              label="Avaliação de crédito"
              {...register("avaliacaoCredito")}
            >
              <option value="">Selecione a avaliação</option>
              <option value="pedido_avaliacao_de_credito_sim">Sim</option>
              <option value="pedido_avaliacao_de_credito_não">Não</option>
            </Select>
            {errors.avaliacaoCredito && (
              <span className="text-red-500 text-sm">
                {errors.avaliacaoCredito.message}
              </span>
            )}
          </div>
          <div>
            <Select
              value={watch("plataforma")}
              label="Plataforma"
              {...register("plataforma")}
            >
              <option value="">Selecione a plataforma</option>
              {plataformas.map((option, index) => (
                <option key={index} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            {errors.plataforma && (
              <span className="text-red-500 text-sm">
                {errors.plataforma.message}
              </span>
            )}
          </div>

          <div>
            <Select
              value={watch("retira")}
              label="Retira"
              {...register("retira")}
            >
              <option value="">Selecione opção de retirada</option>
              {retiraValues.map((option, index) => (
                <option key={index} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            {errors.retira && (
              <span className="text-red-500 text-sm">
                {errors.retira.message}
              </span>
            )}
          </div>
          <Controller
            control={control}
            name="numeroParcelas"
            defaultValue={0}
            render={({ field }) => (
              <div>
                <InputNumber
                  label="Número de parcelas"
                  min={0}
                  step={1}
                  {...field}
                  onIncrement={(value) => {
                    // Capturar posição do scroll antes da atualização
                    const currentScrollTop =
                      window.pageYOffset || document.documentElement.scrollTop;

                    setValue("numeroParcelas", value);

                    // Restaurar posição com múltiplas tentativas
                    const restoreScroll = () => {
                      window.scrollTo({
                        top: currentScrollTop,
                        behavior: "auto",
                      });
                    };

                    setTimeout(restoreScroll, 10);
                    setTimeout(restoreScroll, 50);
                    setTimeout(restoreScroll, 100);
                  }}
                  onDecrement={(value) => {
                    // Capturar posição do scroll antes da atualização
                    const currentScrollTop =
                      window.pageYOffset || document.documentElement.scrollTop;

                    setValue("numeroParcelas", value);

                    // Restaurar posição com múltiplas tentativas
                    const restoreScroll = () => {
                      window.scrollTo({
                        top: currentScrollTop,
                        behavior: "auto",
                      });
                    };

                    setTimeout(restoreScroll, 10);
                    setTimeout(restoreScroll, 50);
                    setTimeout(restoreScroll, 100);
                  }}
                />
                {errors.numeroParcelas && (
                  <span className="text-red-500 text-sm">
                    {errors.numeroParcelas.message}
                  </span>
                )}
              </div>
            )}
          />
          <Checkbox label="ECOM PJ" {...register("ecomPj")} />

          <div>
            <Textarea
              value={watch("observacoes")}
              label="Observações"
              placeholder="Digite as observações"
              {...register("observacoes")}
            />
            {errors.observacoes && (
              <span className="text-red-500 text-sm">
                {errors.observacoes.message}
              </span>
            )}
          </div>

          <div>
            <Select
              value={watch("statusIntegracao")}
              label="Status da integração"
              {...register("statusIntegracao")}
            >
              <option value="">Selecione o status</option>
              {statusIntegracao.map((option, index) => (
                <option key={index} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            {errors.statusIntegracao && (
              <span className="text-red-500 text-sm">
                {errors.statusIntegracao.message}
              </span>
            )}
          </div>

          <div>
            <Select
              value={watch("tipoRegistro")}
              label="Tipo do registro do pedido"
              {...register("tipoRegistro")}
            >
              <option value="">Selecione o tipo</option>
              <option value="pedido_de_troca">Implantação</option>
              <option value="pedido_de_venda">Pedido de venda</option>
            </Select>
            {errors.tipoRegistro && (
              <span className="text-red-500 text-sm">
                {errors.tipoRegistro.message}
              </span>
            )}
          </div>

          <div>
            <Select
              value={watch("tipoDoTicket")}
              label="Tipo do ticket"
              {...register("tipoDoTicket")}
            >
              <option value="">Selecione o tipo do ticket</option>
              {tipoTicketOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            {errors.tipoDoTicket && (
              <span className="text-red-500 text-sm">
                {errors.tipoDoTicket.message}
              </span>
            )}
          </div>

          <div>
            <Input
              label="Categoria do ticket"
              placeholder="Digite a categoria do ticket"
              {...register("categoriaTicket")}
            />
            {errors.categoriaTicket && (
              <span className="text-red-500 text-sm">
                {errors.categoriaTicket.message}
              </span>
            )}
          </div>

          <FixedFooter pageRef={pageRef}>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Processando..." : "Prosseguir"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/")}
              disabled={isLoading}
            >
              Cancelar
            </Button>
          </FixedFooter>
        </form>
      )}

      {step === 2 && (
        <form className="flex flex-col gap-4 mb-4">
          <ProductSearch
            onSelect={(product: ProductResult) => {
              // Capturar posição do scroll antes de adicionar o produto
              const currentScrollTop =
                window.pageYOffset || document.documentElement.scrollTop;

              console.log(
                "🔧 DEBUG: ProductSearch onSelect chamado com:",
                product
              );
              console.log(
                "🔧 DEBUG: Scroll atual antes de adicionar:",
                currentScrollTop
              );

              // Adicionar o produto
              addProduct(product, 1, "");

              // Usar múltiplas tentativas para restaurar o scroll
              // pois o re-render pode demorar um pouco mais
              const restoreScroll = () => {
                window.scrollTo({
                  top: currentScrollTop,
                  behavior: "auto",
                });
              };

              // Tentar restaurar várias vezes com delays diferentes
              setTimeout(restoreScroll, 50);
              setTimeout(restoreScroll, 100);
              setTimeout(restoreScroll, 200);
              setTimeout(restoreScroll, 300);

              console.log("✅ DEBUG: Scroll restoration scheduled");
            }}
            placeholder="Busque por nome, código ou SKU"
          />

          <Divider />

          {selectedProducts.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              Nenhum produto selecionado ainda.
            </div>
          )}

          {selectedProducts.map((product, index) => (
            <div
              key={product.id}
              className="flex flex-col py-2 px-4 border border-[#D8DCDE] rounded gap-4"
              style={{
                scrollBehavior: "auto", // Prevenir smooth scrolling
              }}
            >
              <h4 className="font-semibold text-sm">Produto {index + 1}</h4>
              <Input
                label="Nome"
                value={product.name}
                placeholder="Nome do produto"
                readOnly
              />
              <div className="grid grid-cols-2 gap-2 w-full">
                <InputNumber
                  fullSize
                  label="Quantidade"
                  value={product.quantidade}
                  min={1}
                  onChange={(e) => {
                    const value = parseInt(e.target.value) || 1;
                    updateProduct(product.id, { quantidade: value });
                  }}
                  onIncrement={(value) => {
                    // Capturar posição do scroll antes da atualização
                    const currentScrollTop =
                      window.pageYOffset || document.documentElement.scrollTop;

                    updateProduct(product.id, { quantidade: value });

                    // Restaurar posição com múltiplas tentativas
                    const restoreScroll = () => {
                      window.scrollTo({
                        top: currentScrollTop,
                        behavior: "auto",
                      });
                    };

                    setTimeout(restoreScroll, 10);
                    setTimeout(restoreScroll, 50);
                    setTimeout(restoreScroll, 100);
                  }}
                  onDecrement={(value) => {
                    // Capturar posição do scroll antes da atualização
                    const currentScrollTop =
                      window.pageYOffset || document.documentElement.scrollTop;

                    updateProduct(product.id, { quantidade: value });

                    // Restaurar posição com múltiplas tentativas
                    const restoreScroll = () => {
                      window.scrollTo({
                        top: currentScrollTop,
                        behavior: "auto",
                      });
                    };

                    setTimeout(restoreScroll, 10);
                    setTimeout(restoreScroll, 50);
                    setTimeout(restoreScroll, 100);
                  }}
                />
                <Input
                  label="Valor Uni."
                  value={product.valorUnitario}
                  placeholder="Ex.: 100.00"
                  required={false}
                  onChange={(e) =>
                    updateProduct(product.id, { valorUnitario: e.target.value })
                  }
                />
              </div>

              <div>
                <Button
                  type="button"
                  variant="outline-danger"
                  onClick={() => removeProduct(product.id)}
                >
                  Remover
                </Button>
              </div>
            </div>
          ))}

          <FixedFooter pageRef={pageRef}>
            <Button
              type="button"
              onClick={() => setStep((prev) => prev + 1)}
              disabled={selectedProducts.length === 0}
            >
              Prosseguir
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setStep((prev) => prev - 1)}
            >
              Voltar
            </Button>
          </FixedFooter>
        </form>
      )}

      {step === 3 && (
        <div className="mb-4">
          {savedOrderData ? (
            <OrderSummary
              orderData={savedOrderData}
              products={selectedProducts}
            />
          ) : (
            <Accordion
              title="Pedido 1"
              fields={fields}
              values={values}
              defaultOpen={true}
            />
          )}

          <FixedFooter pageRef={pageRef}>
            <Button
              onClick={handleFinalSubmit}
              disabled={
                isLoading || !savedOrderData || selectedProducts.length === 0
              }
            >
              {isLoading ? "Processando..." : "Adicionar pedido"}
            </Button>
            <Button
              variant="outline"
              onClick={() => setStep((prev) => prev - 1)}
              disabled={isLoading}
            >
              Voltar
            </Button>
          </FixedFooter>
        </div>
      )}
    </div>
  );
}
